import { useState } from 'react';
import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Card, Flex, Typography, theme } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import type { MonetizationCustomerDetailOut } from '@/api/generated/schemas';
import { orDash } from '@/components/common/show/shared';
import { extractBackendMessage } from '@/utils/apiError';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

interface CustomerRevenueCatCardProps {
  record: MonetizationCustomerDetailOut;
}

/** Source-record card: where this customer lives in RevenueCat + resync. */
export const CustomerRevenueCatCard = ({
  record,
}: CustomerRevenueCatCardProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await mutateAsync({
        url: `monetization/customers/${record.id}/sync`,
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: t('monetization.customers.sync.success'),
      });
      invalidate({
        resource: 'monetization.customers',
        invalidates: ['list', 'detail'],
      });
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description:
          extractBackendMessage(error) ??
          t('monetization.customers.sync.error_fallback'),
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Card title={t('monetization.customers.show.revenuecat')}>
      <Flex vertical gap={token.marginSM}>
        <Text type="secondary">
          {t('monetization.customers.fields.last_synced_at')}:{' '}
          {orDash(formatDateTime(record.last_synced_at))}
        </Text>

        <Button
          block
          icon={<SyncOutlined spin={syncing} />}
          loading={syncing}
          onClick={handleSync}
        >
          {t('monetization.customers.actions.sync')}
        </Button>
      </Flex>
    </Card>
  );
};
